import WebSocketStore from 'core/classes/services/WebSocketStore'
import { IAmsLATAMSearchResponse } from 'onboarding/classes/models/aplAmsLatam'
import { DateHelper } from 'onboarding/utils/dateHelper'
import { extractFileNameFromContentDisposition } from 'onboarding/utils/headers'
import {
  ENDPOINT_EXPORT_APL_AMS_ELIGIBLE_PARTNER_LIST,
  ENDPOINT_EXPORT_APL_AMS_LATAM_ELIGIBLE_PARTNER_LIST,
} from 'src/config/url'

class APLApi {
  public async exportAMSEligiblePartners(token: string) {
    const response = await fetch(ENDPOINT_EXPORT_APL_AMS_ELIGIBLE_PARTNER_LIST, {
      method: 'GET',
      headers: {
        Authorization: token,
      },
    })
    if (!response.ok) {
      const errorMessage = await response.text()
      throw new Error(errorMessage || 'Failed to export APL AMS eligible partners')
    }

    const blob = await response.blob()

    return {
      fileName: extractFileNameFromContentDisposition(
        response,
        `${DateHelper.formatToType(new Date(), 'file')}_apl_ams_eligible_partner_list.xlsx`
      ),
      file: blob,
    }
  }

  public async exportAmsLatamEligiblePartners(params: IAplAmsLatamExportParams, token: string) {
    const response = await fetch(ENDPOINT_EXPORT_APL_AMS_LATAM_ELIGIBLE_PARTNER_LIST, {
      method: 'POST',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    })
    if (!response.ok) {
      const errorMessage = await response.text()
      throw new Error(errorMessage || 'Failed to export APL AMS LATAM eligible partners')
    }

    const blob = await response.blob()

    return {
      fileName: extractFileNameFromContentDisposition(
        response,
        `${DateHelper.formatToType(new Date(), 'file')}_apl_ams_latam_eligible_partner_list.xlsx`
      ),
      file: blob,
    }
  }

  public async findOwnSprRegistration(
    query: IOwnSprRegistrationQuery
  ): Promise<IOwnSprRegistration | null> {
    const response = await WebSocketStore.get('onboarding').call<Partial<IOwnSprRegistration>>({
      service: 'spr',
      action: 'check-own-registration',
      payload: query,
    })

    if (response.status !== 'success') {
      throw new Error(`spr:check-own-registration answered ${response.status}`)
    }

    const requestId: unknown = response.data?.RequestId

    return typeof requestId === 'number' ? { RequestId: requestId } : null
  }

  public async checkAccessAmsSearch() {
    const response = await WebSocketStore.get('onboarding').call({
      service: 'apl',
      action: 'check-access-ams-search',
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    return response.status === 'success'
  }

  public async searchLA(payload: IAplAmsLaSearchParams) {
    const response = await WebSocketStore.get('onboarding').call<{
      items: IAmsLATAMSearchResponse[]
    }>({
      service: 'apl',
      action: 'ams-latam-search',
      payload,
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to search')
    }

    return response.data.items
  }
}

export const aplApi = new APLApi()

export interface IOwnSprRegistrationQuery {
  excludeRequestId?: number
  includeRejected: boolean
}

export interface IOwnSprRegistration {
  RequestId: number
}

export interface IAplAmsLaSearchParams {
  companyName?: string
  countryISO?: string
  taxId?: string
  page: number
  size: number
  skipTotal: boolean
  sort: {
    field: string
    dir: 'asc' | 'desc'
  }[]
}

export interface IAplAmsLatamExportParams {
  communicationName?: string
  countryISO?: string
  taxId?: string
}
